import React from 'react';
import {
  Alert, Box, Button, Card, CardContent, Grid, Paper, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';

import {
  buildScenarioAssumptions,
  fetchScenarioAssumptions,
  fetchScenarioAssumptionSummary,
  saveScenarioAssumptions,
} from './bieScenarioAssumptionApi';

import BieCollapsibleSection from './BieCollapsibleSection';

type Props = { scenarioCode?: string };

type State = {
  scenarioCode: string;
  tab: number;
  rows: any[];
  summary: any;
  loading: boolean;
  error: string;
  success: string;
};

const money = (v: any) => {
  if (v === null || v === undefined || v === '') return '';
  return Number(v || 0).toLocaleString();
};

const parseNumber = (v: any) => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).replace(/,/g, '').trim();
  if (s === '') return '';
  const n = Number(s);
  return Number.isNaN(n) ? '' : n;
};

class BieSimulatorAssumptionsPanel extends React.Component<Props, State> {
  state: State = {
    scenarioCode: this.props.scenarioCode || 'DET2027_BASE',
    tab: 0,
    rows: [],
    summary: {},
    loading: false,
    error: '',
    success: '',
  };

  componentDidMount() {
    this.refresh();
  }

  setScenarioCode = (e: any) => this.setState({ scenarioCode: String(e.target.value || '').toUpperCase() });
  setTab = (_: any, tab: number) => this.setState({ tab });

  build = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      await buildScenarioAssumptions(this.state.scenarioCode);
      await this.refresh();
      this.setState({ success: 'Scenario checklist built successfully.' });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  refresh = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [rows, summary] = await Promise.all([
        fetchScenarioAssumptions(this.state.scenarioCode),
        fetchScenarioAssumptionSummary(this.state.scenarioCode),
      ]);
      this.setState({ rows, summary });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  updateRow = (rid: number, field: 'mqt' | 'mam' | 'rmk', value: any) => {
    this.setState({
      rows: this.state.rows.map((r: any) =>
        r.rid === rid ? { ...r, [field]: field === 'rmk' ? value : parseNumber(value), __dirty: true } : r),
    });
  };

  addOptionalRow = () => {
    this.setState({
      tab: 3,
      rows: [...this.state.rows, {
        rid: null, drc: '', drn: '', rgr: 'OPTIONAL MANUAL INPUTS', src: 'MANUAL',
        req: false, mqt: '', mam: '', rmk: '', sus: 'MANUAL', rst: 'OPTIONAL',
        __new: true, __dirty: true,
      }],
    });
  };

  updateNewRow = (index: number, field: string, value: any) => {
    const rows = [...this.state.rows];
    rows[index] = {
      ...rows[index],
      [field]: field === 'mqt' || field === 'mam' ? parseNumber(value) : value,
      __dirty: true,
    };
    this.setState({ rows });
  };

  save = async () => {
    const changed = this.state.rows.filter((r: any) => r.__dirty).map((r: any) => ({
      rid: r.rid || null,
      drc: r.drc,
      mqt: r.mqt === '' ? null : r.mqt,
      mam: r.mam === '' ? null : r.mam,
      rmk: r.rmk || '',
    }));

    if (changed.length === 0) {
      this.setState({ success: 'No changes to save.' });
      return;
    }

    if (changed.find((r: any) => !r.rid && !r.drc)) {
      this.setState({ error: 'Optional rows must have a driver code.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });
    try {
      await saveScenarioAssumptions(this.state.scenarioCode, changed);
      await this.refresh();
      this.setState({ success: `${changed.length} assumption row(s) saved.` });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  runSimulation = () => {
    if (Number(this.state.summary?.missing || 0) > 0) {
      this.setState({ error: 'Cannot run simulation. Required assumptions are still missing.' });
      return;
    }
    this.setState({ success: 'Checklist is ready. Run the BIE Simulator recompute action from the simulator toolbar.' });
  };

  summaryCards() {
    const s = this.state.summary || {};
    const items = [
      ['Total Inputs', s.total || 0], ['Required', s.required || 0], ['Ready', s.ready || 0],
      ['Missing', s.missing || 0], ['Optional', s.optional || 0], ['Manual Overrides', s.manual_overrides || 0],
    ];

    return (
      <Grid container spacing={2}>
        {items.map(([label, value]) => (
          <Grid size={{ xs: 12, sm: 6, md: 2 }} key={String(label)}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  filteredRows() {
    const { rows, tab } = this.state;
    if (tab === 1) return rows.filter((r: any) => r.req === true);
    if (tab === 2) return rows.filter((r: any) => r.rst === 'MISSING');
    if (tab === 3) return rows.filter((r: any) => r.sus === 'MANUAL' || r.__new);
    return rows;
  }

  renderGrid() {
    const rows = this.filteredRows();

    return (
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Tabs value={this.state.tab} onChange={this.setTab}>
            <Tab label="All" />
            <Tab label="Required" />
            <Tab label="Missing" />
            <Tab label="Manual / Optional" />
          </Tabs>
          <Button variant="outlined" onClick={this.addOptionalRow}>Add Optional Row</Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Driver</TableCell><TableCell>Group</TableCell><TableCell>Required</TableCell>
              <TableCell>Source Category</TableCell>
              <TableCell align="right">Forecast Qty</TableCell>
              <TableCell align="right">Forecast Amt</TableCell>
              <TableCell align="right">Derived Qty</TableCell>
              <TableCell align="right">Derived Amt</TableCell>
              <TableCell align="right">API Qty</TableCell>
              <TableCell align="right">API Amt</TableCell>
              <TableCell align="right">Manual Qty</TableCell>
              <TableCell align="right">Manual Amt</TableCell>
              <TableCell align="right">System Qty</TableCell>
              <TableCell align="right">System Amt</TableCell>
              <TableCell align="right">Effective Qty</TableCell>
              <TableCell align="right">Effective Amt</TableCell>
              <TableCell>Source Used</TableCell>
              <TableCell>Status</TableCell><TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r: any, i: number) => {
              const originalIndex = this.state.rows.indexOf(r);
              return (
                <TableRow key={r.rid || `new-${i}`} hover>
                  <TableCell sx={{ minWidth: 160 }}>
                    {r.__new ? (
                      <TextField size="small" value={r.drc || ''} placeholder="DRIVER"
                        onChange={(e) => this.updateNewRow(originalIndex, 'drc', String(e.target.value || '').toUpperCase())} />
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.drc}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.drn}</Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>{r.rgr}</TableCell><TableCell>{r.req ? 'Yes' : 'No'}</TableCell><TableCell>{r.src}</TableCell>
                  <TableCell align="right">{money(r.fqt)}</TableCell><TableCell align="right">{money(r.fam)}</TableCell>
                  <TableCell align="right">{money(r.yqt)}</TableCell><TableCell align="right">{money(r.yam)}</TableCell>
                  <TableCell align="right">{money(r.aqt)}</TableCell><TableCell align="right">{money(r.aam)}</TableCell>
                  <TableCell align="right"><TextField size="small" value={r.mqt ?? ''} sx={{ width: 120 }}
                    onChange={(e) => r.__new ? this.updateNewRow(originalIndex, 'mqt', e.target.value) : this.updateRow(r.rid, 'mqt', e.target.value)} /></TableCell>
                  <TableCell align="right"><TextField size="small" value={r.mam ?? ''} sx={{ width: 140 }}
                    onChange={(e) => r.__new ? this.updateNewRow(originalIndex, 'mam', e.target.value) : this.updateRow(r.rid, 'mam', e.target.value)} /></TableCell>
                  
                  <TableCell align="right">{money(r.sqt)}</TableCell><TableCell align="right">{money(r.sam)}</TableCell>
                  <TableCell align="right">{money(r.eqt)}</TableCell><TableCell align="right">{money(r.eam)}</TableCell>
                  <TableCell>{r.sus}</TableCell>
                  <TableCell><Typography variant="body2" color={r.rst === 'MISSING' ? 'error' : r.rst === 'READY' ? 'success.main' : 'text.secondary'} sx={{ fontWeight: 600 }}>{r.rst}</Typography></TableCell>
                  <TableCell><TextField size="small" value={r.rmk || ''} sx={{ minWidth: 180 }}
                    onChange={(e) => r.__new ? this.updateNewRow(originalIndex, 'rmk', e.target.value) : this.updateRow(r.rid, 'rmk', e.target.value)} /></TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={17}><Typography variant="body2" color="text.secondary">No assumptions found. Click Build Checklist.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  // renderChecklistTable() {
  //   return (
  //     <>
  //       {/* old Table/TableContainer/DataGrid content */}
  //     </>
  //   );
  // }

  // renderCollapsibleGrid() {

  //   return (
  //     <Stack spacing={2}>
  //       {/* Keep existing summary/header cards visible */}
  //       {this.renderSummaryCards?.()}

  //       <BieCollapsibleSection
  //         title="Checklist Editor"
  //         subtitle="Detailed assumptions and readiness checks. Collapse this section to focus on the simulator mixer and indicators."
  //         defaultOpen={false}
  //       >
  //         {this.renderChecklistTable
  //           ? this.renderChecklistTable()
  //           : (
  //             <>
  //               {/* Move your existing checklist Table/DataGrid JSX here */}
  //             </>
  //           )}
  //         {this.renderGrid
  //           ? this.renderGrid()
  //           : (
  //             <>
  //               {/* Move your existing checklist Table/DataGrid JSX here */}
  //             </>
  //           )}
  //       </BieCollapsibleSection>
  //     </Stack>
  //   );

  // }

  // render0() {
  //   const { scenarioCode, loading, error, success } = this.state;
  //   return (
  //     <Box sx={{ p: 2 }}>
  //       <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
  //         <Box>
  //           <Typography variant="h5" sx={{ fontWeight: 700 }}>Scenario Readiness / Assumptions</Typography>
  //           <Typography variant="body2" color="text.secondary">
  //             Review required simulator inputs, override forecast or derived values, and confirm readiness before simulation.
  //           </Typography>
  //         </Box>
  //         <Stack direction="row" spacing={1}>
  //           <TextField size="small" label="Scenario" value={scenarioCode} onChange={this.setScenarioCode} />
  //           <Button variant="contained" onClick={this.build} disabled={loading}>Build Checklist</Button>
  //           <Button variant="outlined" onClick={this.refresh} disabled={loading}>Refresh</Button>
  //           <Button variant="contained" color="success" onClick={this.save} disabled={loading}>Save Overrides</Button>
  //           <Button variant="outlined" color="secondary" onClick={this.runSimulation} disabled={loading}>Run Simulation</Button>
  //         </Stack>
  //       </Stack>
  //       {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
  //       {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
  //       <Stack spacing={2}>{this.summaryCards()}{this.renderGrid()}</Stack>
  //     </Box>
  //   );
  // }

  render() {
    const { scenarioCode, loading, error, success } = this.state;
    return (
      <Box sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Scenario Readiness / Assumptions</Typography>
            <Typography variant="body2" color="text.secondary">
              Review required simulator inputs, override forecast or derived values, and confirm readiness before simulation.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="Scenario" value={scenarioCode} onChange={this.setScenarioCode} />
            <Button variant="contained" onClick={this.build} disabled={loading}>Build Checklist</Button>
            <Button variant="outlined" onClick={this.refresh} disabled={loading}>Refresh</Button>
            <Button variant="contained" color="success" onClick={this.save} disabled={loading}>Save Overrides</Button>
            <Button variant="outlined" color="secondary" onClick={this.runSimulation} disabled={loading}>Run Simulation</Button>
          </Stack>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Stack spacing={2}>
          {this.summaryCards()}
          <BieCollapsibleSection
            title="Checklist Editor"
            subtitle="Detailed assumptions and readiness checks. Collapse this section to focus on the simulator mixer and indicators."
            defaultOpen={true}
          >
            {this.renderGrid()}
          </BieCollapsibleSection>
        </Stack>
      </Box>
    );
  }
}

export default BieSimulatorAssumptionsPanel;
